import { useEffect, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { StudioShell } from "../components/StudioShell";
import { UploadZone } from "../components/UploadZone";
import { StyleSelect } from "../components/StyleSelect";
import { MaterialPicker } from "../components/MaterialPicker";
import { toast } from "sonner";
import { Eye } from "lucide-react";

const MODELS = [
  "a tall female model with dark hair",
  "a male model with an athletic build",
  "a female model with curly hair and medium build",
  "a plus-size female model",
  "a young male model with short blond hair",
  "a mature female model with grey hair",
];

const SETTINGS = [
  "in a bright photo studio with seamless white backdrop",
  "on a Paris street at golden hour",
  "on a fashion runway with dramatic lighting",
  "on a Mediterranean beach at sunset",
  "in a minimalist concrete gallery space",
  "on a rainy Tokyo street at night, neon lights",
  "in an autumn park with soft natural light",
  "in a luxury hotel lobby",
];

export default function TryOnStudio() {
  const [designs, setDesigns] = useState([]);
  const [source, setSource] = useState("design");
  const [designId, setDesignId] = useState("");
  const [garment, setGarment] = useState(null);
  const [model, setModel] = useState(MODELS[0]);
  const [setting, setSetting] = useState(SETTINGS[0]);
  const [instructions, setInstructions] = useState("");
  const [styleId, setStyleId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [fittingRecords, setFittingRecords] = useState([]);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
    api.get("/fitting-records").then((r) => setFittingRecords(r.data)).catch(() => {});
  }, []);

  const canGenerate = source === "design" ? designId : garment;

  const generate = async () => {
    setGenerating(true);
    try {
      const body = { model_desc: model, setting, instructions, style_id: styleId || null, material_id: materialId || null };
      if (source === "design") body.design_id = designId; else body.garment_b64 = garment;
      const { data } = await api.post("/generate/tryon", body);
      setResult(data);
      toast.success("Try-on generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <StudioShell title="Virtual Model Try-On" subtitle="Visualize outfits on models in any setting"
      onGenerate={generate} generating={generating} disabled={!canGenerate} result={result} generateLabel="Generate try-on">
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Garment source</label>
        <div className="mt-2 flex gap-2">
          <button data-testid="tryon-source-design" onClick={() => setSource("design")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${source === "design" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>From my designs</button>
          <button data-testid="tryon-source-upload" onClick={() => setSource("upload")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${source === "upload" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>Upload image</button>
        </div>
      </div>
      {source === "design" ? (
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Select design</label>
          <div className="mt-2 grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
            {designs.length === 0 && <p className="text-xs text-zinc-400 col-span-3">No designs yet — create one in Sketch to Render.</p>}
            {designs.map((d) => (
              <button key={d.id} data-testid={`tryon-design-${d.id}`} onClick={() => setDesignId(d.id)}
                className={`border rounded-md overflow-hidden text-left transition-colors ${designId === d.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-20 object-cover" />
                <p className="text-[10px] p-1.5 truncate">{d.title}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <UploadZone value={garment} onChange={setGarment} label="Upload garment image" testId="tryon-garment-upload" />
      )}
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Model</label>
        <select data-testid="tryon-model-select" value={model} onChange={(e) => setModel(e.target.value)}
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
          {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Setting / Location</label>
        <select data-testid="tryon-setting-select" value={setting} onChange={(e) => setSetting(e.target.value)}
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
          {SETTINGS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <MaterialPicker value={materialId} onChange={setMaterialId} testIdPrefix="tryon-material" />
      <StyleSelect value={styleId} onChange={setStyleId} />
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Styling notes (optional)</label>
        <textarea data-testid="tryon-instructions-input" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
          placeholder="e.g. pair with white sneakers, add sunglasses"
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
      </div>
      {/* Try-on result gallery */}
      <div className="border-t border-zinc-200 pt-4 mt-2">
        <button onClick={() => setShowGallery(!showGallery)}
          className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-900">
          <Eye size={12} /> Try-on results ({fittingRecords.length})
        </button>
        {showGallery && fittingRecords.length > 0 && (
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {fittingRecords.map((f) => (
              <div key={f.id} className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Techpack: {f.techpack_id?.slice(0, 8)}...</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${(f.fit_assessment?.overall_fit === "good") ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                    {f.fit_assessment?.overall_fit || "N/A"}
                  </span>
                </div>
                {f.drape_setting && <p className="text-[10px] text-zinc-400 mt-1">Drape: {f.drape_setting}</p>}
                {(f.rendered_images || []).length > 0 && (
                  <div className="mt-2 flex gap-1 overflow-x-auto">
                    {f.rendered_images.map((img, i) => (
                      <img key={i} src={img.url || img.image_url || ""} alt={`render-${i}`} className="w-12 h-12 rounded border border-zinc-200 object-cover" />
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-zinc-400 mt-1">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</p>
              </div>
            ))}
          </div>
        )}
        {showGallery && fittingRecords.length === 0 && (
          <p className="mt-2 text-xs text-zinc-400">No fitting results yet.</p>
        )}
      </div>
    </StudioShell>
  );
}
